import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import $utils from '@/utils';
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const { loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 20, ordering: '-movement_date' };

function paramsWithProductFormat(filters = {}) {
    const params = { ...filters };
    const legacy = params.format;
    if (
        legacy != null
        && legacy !== ''
        && legacy !== 'csv'
        && (params.product_format == null || params.product_format === '')
    ) {
        params.product_format = legacy;
    }
    if (legacy !== 'csv') {
        delete params.format;
    }
    return params;
}

const createState = (options = {}) => {
    return {
        movements: [],
        movements_filters: DEFAULT_FILTERS,
        movements_count: 0,
        movements_loaded: false,
        movements_loading: false,
        movements_loading_errors: null,
        movements_cancel_source: null,

        movements_stats: {},
        movements_stats_loaded: false,
        movements_stats_loading: false,
        movements_stats_loading_errors: null,
        movements_stats_cancel_source: null,

        movement_deleting: false,
        movement_deleting_errors: null,
        
        _storageOptions: options || {},
    };
};


const mutations = {
    updateMovements(state, movements) {
        state.movements = movements;
    },
    updateMovementsCount(state, count) {
        state.movements_count = count;
    },
    updateMovementsFilters(state, filters) {
        if (filters?.movement_type !== "adjustment") {
            filters.adjustment_reason = null;
        }
        state.movements_filters = { ...filters };
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.movements_filters, fieldsToSave);
        }
    },
    updateMovementsLoading(state, loading) {
        state.movements_loading = loading;
    },
    updateMovementsLoadingErrors(state, errors) {
        state.movements_loading_errors = errors;
    },
    updateMovementsCancelSource(state, cancelSource) {
        state.movements_cancel_source = cancelSource;
    },

    updateMovementsStats(state, movements_stats) {
        state.movements_stats = movements_stats;
        state.movements_stats_loaded = true;
    },
    updateMovementsStatsLoading(state, loading) {
        state.movements_stats_loading = loading;
    },
    updateMovementsStatsLoadingErrors(state, errors) {
        state.movements_stats_loading_errors = errors;
    },
    updateMovementsStatsCancelSource(state, cancelSource) {
        state.movements_stats_cancel_source = cancelSource;
    },

    updateMovementDeleting(state, deleting) {
        state.movement_deleting = deleting;
    },
    updateMovementDeletingErrors(state, errors) {
        state.movement_deleting_errors = errors;
    },
    updateMovementsLoaded(state, loaded) {
        state.movements_loaded = loaded;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },
};

const actions = {

    exportMovementsAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters = paramsWithProductFormat(state.movements_filters);
            filters.offset = null;
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;
        
        $utils.downloadFile({
            url: "/api/movements/",
            params: filters,
        }, $utils.getFilename('movements'));
    },

    exportInventoryAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters = paramsWithProductFormat(state.movements_filters);
            filters.offset = null;
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;
        
        $utils.downloadFile({
            url: "/api/movements/inventory/",
            params: filters,
        }, $utils.getFilename('movementsInventory'));
    },

    async fetchMovements({ commit, dispatch, state }, params) {
        commit('updateMovementsLoading', true);
        commit('updateMovementsLoadingErrors', null);

        if (state.movements_cancel_source) {
            state.movements_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateMovementsCancelSource', cancelSource);

        try {
            const response = await axios.get('/api/movements/', {
                params: paramsWithProductFormat(state.movements_filters),
                cancelToken: state.movements_cancel_source?.token
            });
            commit('updateMovements', response.data.results);
            commit('updateMovementsCount', response.data.count);
            commit('updateMovementsCancelSource', null);
            dispatch('fetchMovementsStats');
            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateMovementsLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateMovementsLoading', false);
            commit('updateMovementsLoaded', true);
        }
    },

    async fetchMovementsStats({ commit, state }) {
        commit('updateMovementsStatsLoading', true);
        commit('updateMovementsStatsLoadingErrors', null);

        if (state.movements_stats_cancel_source) {
            state.movements_stats_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateMovementsStatsCancelSource', cancelSource);

        try {
            const response = await axios.get('/api/movements/stats/', {
                params: paramsWithProductFormat(state.movements_filters),
                cancelToken: state.movements_stats_cancel_source?.token
            });
            commit('updateMovementsStatsLoading', false);
            commit('updateMovementsStats', response.data);
            commit('updateMovementsStatsCancelSource', null);
            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateMovementsStatsLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateMovementsStatsLoading', false);
        }
    },

    async init({ commit, dispatch, state }, params) {
        commit('updateMovementsLoading', true);
        commit('updateMovementsLoaded', false);
        commit('updateMovements', []);
        commit('updateMovementsCount', 0);
        
        // Update storage options if provided
        if (params?.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        // Load filters from localStorage if storage is configured
        await commit('updateMovementsFilters', loadFiltersFromStorage(state._storageOptions?.storageKey, params?.filters || {}));
    }
};

export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions
});